// libraries
import React, { useRef, useState, useEffect } from 'react'
import clsx from 'clsx'

// components
import Image from '/react4xp/components/Image'
import Button from '/react4xp/components/Button'
import Popup from '/react4xp/components/Popup'
import RichText from '/react4xp/components/RichText'

// svg
import { GeneralHeart, GeneralChat, GeneralTrash } from '/react4xp/components/Svg/Icons/General'
import { UxArrowRight } from '/react4xp/components/Svg/Ux/Arrow'

// utils
import { isNonWhitespaceString, handleAnonymousContentLiked } from '/lib/reactutils/utils'

// prop types
export interface IdeaCardProps {
    className?: string
    image?: string
    alt?: string
    status?: 'new-idea' | 'in-process' | 'rejected' | 'released' | 'other'
    statusText?: string
    title?: string
    extraField?: string
    desc?: string
    likes?: string
    isLiked?: boolean
    comments?: string
    href?: string
    showExclude?: boolean
    popup?: {
        exclude: {
            title: string
            content: string
            endpoint: string
            onSuccess?: () => void
        },
        excludeError: {
            title: string
            content: string
        }
    }
    isInteractive?: boolean
    services?: {
        likeContent?: string
    }
    isLogged?: boolean
    contentId?: string
    strings?: {
        deleteIdeaButton?: string
    }
}

export default function IdeaCard({
    className,
    image,
    alt,
    status,
    statusText,
    title,
    extraField,
    desc,
    likes,
    isLiked,
    comments,
    href,
    showExclude,
    popup,
    isInteractive,
    services,
    isLogged,
    contentId,
    strings
}: IdeaCardProps) {

    // refs
    const popupExcludeConfirmation = useRef<HTMLAnchorElement>(null)
    const popupExcludeError = useRef<HTMLAnchorElement>(null)

    // useState to make the modal invisible
    const [renderExcludeConfirmation, setRenderExcludeConfirmation] = useState(false)
    const [renderExcludeError, setRenderExcludeError] = useState(false)

    // close modal
    const closeExcludeModal = () => { setRenderExcludeConfirmation(false) }
    const closeExcludeErrorModal = () => { setRenderExcludeError(false) }

    // open modal
    const openExcludeModal = () => {
        setRenderExcludeConfirmation(true)
        setTimeout(() => {
            popupExcludeConfirmation?.current?.click()
        }, 100)
    }

    const openExcludeErrorModal = () => {
        setRenderExcludeError(true)
        setTimeout(() => {
            popupExcludeError?.current?.click()
        }, 100)
    }

    // fetch endpoint
    const handleExclude = async () => {
        try {
            const response = await fetch(popup?.exclude.endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                }
            })

            const data = await response.json()

            if (response.ok) {
                setTimeout(() => {
                    console.log('success')
                    closeExcludeModal()
                    popup?.exclude.onSuccess?.()
                }, 500)
            } else {
                setTimeout(() => {
                    console.log('error', data)
                    popup?.exclude.onSuccess?.()
                    closeExcludeModal()
                    openExcludeErrorModal()
                }, 500)
            }
        } catch (error) {
            setTimeout(() => {
                console.log('error', error)
                popup?.exclude.onSuccess?.()
                closeExcludeModal()
                openExcludeErrorModal()
            }, 500)
        }
    }

    // handle like
    const [liked, setLiked] = useState(isLiked || false)
    const [likesNumber, setLikesNumber] = useState(likes || 0)
    const toggleLike = async (event) => {
        event.target.disabled = true
        try {
            const response = await fetch(`${services?.likeContent}?contentId=${contentId}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    isLiked: !liked
                })
            })

            const data = await response.json()

            if (response.ok) {
                setLikesNumber(data.totalLikes)
                setLiked(data.isLiked)

                if (!isLogged) {
                    handleAnonymousContentLiked(contentId, data.isLiked)
                }
            }
        } catch (error) {
            console.log('error', error)
        }
        event.target.disabled = false
    }
    useEffect(() => {
        if (isInteractive) {
            try {
                if (!isLogged) {
                    const anonymousContentLiked = localStorage.getItem('anonymous-content-liked')
                    if (anonymousContentLiked) {
                        const anonymousContentLikedData = JSON.parse(anonymousContentLiked)
                        if (anonymousContentLikedData.includes(contentId)) {
                            setLiked(true)
                        } else {
                            setLiked(false)
                        }
                    } else {
                        localStorage.setItem('anonymous-content-liked', JSON.stringify([]))
                        setLiked(false)
                    }
                }
            } catch(err) {
                console.log('error', err)
            }
        }
    }, [])

    return (
        <div className={clsx(
            'idea-card',
            className
        )}>

            <div className='idea-card__wrapper'>

                {image && (
                    <a href={href} className='idea-card__image-wrapper'>
                        <div className='idea-card__image'>
                            <Image
                                src={image}
                                alt={alt}
                                className='cover'
                            />
                        </div>
                    </a>
                )}

                {status && (
                    <div
                        className={clsx(
                            'idea-card__status',
                            status === 'new-idea' && 'bg-blue white',
                            status === 'in-process' && 'bg-green white',
                            status === 'rejected' && 'bg-red white',
                            status === 'released' && 'bg-yellow-dark',
                            status === 'other' && 'bg-blue white',
                        )}
                    >
                        <p className='text-smaller bold'>
                            {statusText}
                        </p>
                    </div>
                )}

                {isNonWhitespaceString(extraField) && (
                    <p className='idea-card__extra-field'>
                        <i>{extraField}</i>
                    </p>
                )}

                {isNonWhitespaceString(title) && (
                    <a href={href} className='idea-card__title'>
                        <h3 className='h4'>
                            {title}
                        </h3>
                    </a>
                )}

                {isNonWhitespaceString(desc) && (
                    <RichText
                        className='idea-card__desc'
                        content={desc}
                    />
                )}

                <div className='idea-card__bottom-icons'>

                    {
                        isInteractive &&
                        <div className='idea-card__bottom-icons__left'>

                            <div className='idea-card__bottom-icons__item'>

                                <button
                                    className={clsx(
                                        'likes'
                                    )}
                                    onClick={async (e) => {
                                        await toggleLike(e)
                                    }}
                                >
                                    <div className={clsx(
                                        'idea-card__bottom-icons__icon',
                                        liked && 'bg-red'
                                    )}>
                                        <GeneralHeart variant='white' />
                                    </div>
                                </button>

                                <span className='text-small'>
                                    {likesNumber || '0'}
                                </span>

                            </div>

                            <div className='idea-card__bottom-icons__item'>

                                <button onClick={() => {
                                    window.open(`${href}#user-review-comments`, '_self')
                                }}>
                                    <div className='idea-card__bottom-icons__icon'>
                                        <GeneralChat variant='white' />
                                    </div>
                                </button>

                                <span className='text-small'>
                                    {comments ? comments : '0'}
                                </span>

                            </div>

                        </div>
                    }
                    {
                        !isInteractive &&
                        <div className='idea-card__bottom-icons__left'>

                            <div className='idea-card__bottom-icons__item'>

                                <div className={clsx(
                                    'idea-card__bottom-icons__icon',
                                    liked && 'bg-red'
                                )}>
                                    <GeneralHeart variant='white' />
                                </div>

                                <span className='text-small'>
                                    {likesNumber || '0'}
                                </span>

                            </div>

                            <div className='idea-card__bottom-icons__item'>

                                <div className='idea-card__bottom-icons__icon'>
                                    <GeneralChat variant='white' />
                                </div>

                                <span className='text-small'>
                                    {comments ? comments : '0'}
                                </span>

                            </div>

                        </div>

                    }

                    <a href={href} className='idea-card__bottom-icons__arrow'>
                        <UxArrowRight />
                    </a>

                </div>

                {showExclude && (
                    <>
                        <Button
                            className='text-small idea-card__exclude'
                            hover='black'
                            color='transparent'
                            hasBorders
                            smallBorders
                            noUnderline
                            onClick={openExcludeModal}
                        >
                            { strings?.deleteIdeaButton || 'Slett idé' }
                            <GeneralTrash variant='outline' />
                        </Button>

                        {renderExcludeConfirmation &&
                            <Popup
                                size='medium'
                                ref={popupExcludeConfirmation}
                                hasCloseButton
								closeButtonText='Lukk'
								title={popup?.exclude.title}
								content={popup?.exclude.content}
                                className='popup--exclude-popup'
								actionButtons={{
                                    primary: {
                                        text: 'Nei',
                                        closeOnClick: true
                                    },
                                    secondary: {
                                        text: 'Ja jeg er sikker',
                                        onClick: () => handleExclude(),
                                        type: 'submit',
                                        closeOnClick: false
                                    }
                                }}
                                onClose={closeExcludeModal}
                            />
                        }

                        {renderExcludeError &&
                            <Popup
                                size='medium'
                                ref={popupExcludeError}
                                hasCloseButton
								closeButtonText='Lukk'
								title={popup?.excludeError.title}
								content={popup?.excludeError.content}
                                className='popup--exclude-popup'
								actionButtons={{
                                    primary: {
                                        text: 'Lukk',
                                        closeOnClick: true
                                    }
                                }}
                                onClose={closeExcludeErrorModal}
                            />
                        }

                    </>
                )}

            </div>

        </div>
    )
}